/**
 * BD1 — client-side password policy + strength scoring.
 *
 * The POLICY mirrors the server exactly (server.py _bd1_password_policy_error:
 * 8–128 chars, ≥1 letter, ≥1 digit) so the UI never accepts what the API
 * would reject. The STRENGTH score is advisory-only UX (meter coloring) and
 * intentionally stricter than the policy — a passing-but-weak password still
 * submits.
 */

export const PW_MIN = 8;
export const PW_MAX = 128;

export function passwordPolicyError(pw: string): string | null {
  if (pw.length < PW_MIN) return `At least ${PW_MIN} characters.`;
  if (pw.length > PW_MAX) return `At most ${PW_MAX} characters.`;
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) {
    return "Include at least one letter and one number.";
  }
  return null;
}

export interface PasswordStrength {
  /** 0 (empty/very weak) … 4 (strong) */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
}

export function passwordStrength(pw: string): PasswordStrength {
  if (!pw) return { score: 0, label: "" };
  const checks = [
    pw.length >= PW_MIN,
    pw.length >= 12,
    /[a-z]/.test(pw) && /[A-Z]/.test(pw),
    /[0-9]/.test(pw),
    /[^A-Za-z0-9]/.test(pw),
  ];
  const score = Math.min(4, checks.filter(Boolean).length) as 1 | 2 | 3 | 4;
  const label =
    score <= 1 ? "Weak" : score === 2 ? "Fair" : score === 3 ? "Good" : "Strong";
  return { score, label };
}
